import React, { useState, useEffect } from 'react';
import { ChromePicker } from 'react-color';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../utils/firebase';

const COLOR_FIELDS = [
  { key: 'primary',    label: 'Primary Colour',    hint: 'Main brand colour (buttons, header, accents)' },
  { key: 'secondary',  label: 'Secondary Colour',  hint: 'Supporting colour (links, highlights)' },
  { key: 'accent',     label: 'Accent Colour',     hint: 'Backgrounds, cards, subtle fills' },
  { key: 'background', label: 'Background',        hint: 'Page background colour' },
  { key: 'text',       label: 'Text Colour',       hint: 'Main body text colour' }
];

function BrandingEditor({ branding, onUpdate, onAutoSave, orgId, onSave, saving, savedMessage }) {
  const [name, setName] = useState(branding?.name || '');
  const [colors, setColors] = useState({ primary: '#3A7D44', secondary: '#B8D9A0', accent: '#EBF5E4', background: '#F5F0E8', text: '#2D3A2E', ...branding?.colors });
  const [openPicker, setOpenPicker] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    setName(branding?.name || '');
    setColors({ primary: '#3A7D44', secondary: '#B8D9A0', accent: '#EBF5E4', background: '#F5F0E8', text: '#2D3A2E', ...branding?.colors });
  }, [branding]);

  const handleNameChange = (e) => {
    setName(e.target.value);
    onUpdate({ ...branding, name: e.target.value });
  };

  const handleColorChange = (color, key) => {
    const newColors = { ...colors, [key]: color.hex };
    setColors(newColors);
    onUpdate({ ...branding, colors: newColors });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (files) => {
      if (!files.length) return;
      setUploading(true);
      setUploadError('');
      try {
        const file = files[0];
        const ts = Date.now();
        const safeName = `${ts}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        const storageRef = ref(storage, `organizations/${orgId || 'default'}/logos/${safeName}`);
        const snapshot = await uploadBytes(storageRef, file, { contentType: file.type || 'image/png' });
        const url = await getDownloadURL(snapshot.ref);
        const img = new window.Image();
        img.onload = async () => {
          const newBranding = { ...branding, logo: { url, width: img.width, height: img.height } };
          onUpdate(newBranding);
          if (onAutoSave) {
            try { await onAutoSave(newBranding); } catch (e) { console.error('Logo auto-save failed:', e); }
          }
          setUploading(false);
        };
        img.onerror = () => {
          // URL is valid even if img load fails; still save what we have
          const newBranding = { ...branding, logo: { url, width: 0, height: 0 } };
          onUpdate(newBranding);
          if (onAutoSave) onAutoSave(newBranding).catch(e => console.error('Logo auto-save failed:', e));
          setUploading(false);
        };
        img.src = url;
      } catch (err) {
        console.error('Logo upload error:', err);
        setUploadError('Failed to upload logo. Please try again.');
        setUploading(false);
      }
    },
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.svg', '.webp'] },
    maxFiles: 1
  });

  const removeLogo = () => onUpdate({ ...branding, logo: { url: '', width: 0, height: 0 } });

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>Brand Identity</h2>
          <p className="bb-editor-desc">Configure your organisation's name, logo and colour palette.</p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      {uploadError && <div className="bb-alert bb-alert--error">{uploadError}</div>}

      {/* Organisation name */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Organisation Name</h3>
        <div className="bb-form-group">
          <label htmlFor="orgName">Display Name <span className="bb-required">*</span></label>
          <input
            id="orgName" type="text" value={name} onChange={handleNameChange}
            placeholder="e.g. WiCare Support Group" className="bb-input" maxLength={100}
          />
          <span className="bb-hint">This appears in the portal header and all communications.</span>
        </div>
      </div>

      {/* Logo upload */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Organisation Logo</h3>
        <div className="bb-logo-area">
          {branding?.logo?.url ? (
            <div className="bb-logo-preview">
              <img src={branding.logo.url} alt="Organisation logo" className="bb-logo-img" />
              <button type="button" className="bb-btn-danger-sm" onClick={removeLogo}>Remove Logo</button>
            </div>
          ) : (
            <div {...getRootProps()} className={`bb-dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}>
              <input {...getInputProps()} />
              {uploading ? (
                <p>Uploading...</p>
              ) : isDragActive ? (
                <p>Drop your logo here...</p>
              ) : (
                <>
                  <div className="bb-dropzone-icon">🖼️</div>
                  <p>Drag & drop your logo, or <span className="bb-link">browse</span></p>
                  <span className="bb-hint">PNG, JPG, SVG or WebP · Max 5MB · Recommended: 300×100px</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Colour palette */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Colour Palette</h3>
        <div className="bb-colors-grid">
          {COLOR_FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="bb-color-item">
              <label>{label}</label>
              <div className="bb-color-row">
                <button
                  type="button"
                  className="bb-color-swatch"
                  style={{ backgroundColor: colors[key] }}
                  onClick={() => setOpenPicker(openPicker === key ? null : key)}
                />
                <span className="bb-color-hex">{colors[key]}</span>
              </div>
              {openPicker === key && (
                <div className="bb-color-picker-wrap">
                  <div className="bb-color-picker-overlay" onClick={() => setOpenPicker(null)} />
                  <div className="bb-color-picker-popup">
                    <ChromePicker
                      color={colors[key]}
                      onChange={(c) => handleColorChange(c, key)}
                      disableAlpha
                    />
                  </div>
                </div>
              )}
              <span className="bb-hint">{hint}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div className="bb-form-section">
        <h3 className="bb-section-title">Live Preview</h3>
        <div className="bb-brand-preview" style={{ '--preview-primary': colors.primary, '--preview-secondary': colors.secondary, '--preview-bg': colors.background, '--preview-text': colors.text }}>
          <div className="bb-preview-header" style={{ backgroundColor: colors.primary }}>
            {branding?.logo?.url
              ? <img src={branding.logo.url} alt={name} style={{ height: '36px', objectFit: 'contain' }} />
              : <span style={{ color: '#fff', fontWeight: 700, fontSize: '18px' }}>{name || 'Organisation Name'}</span>
            }
            <div style={{ display: 'flex', gap: '8px' }}>
              {['Home', 'Events', 'Members'].map(l => (
                <span key={l} style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', cursor: 'pointer' }}>{l}</span>
              ))}
            </div>
          </div>
          <div className="bb-preview-body" style={{ backgroundColor: colors.background, color: colors.text, padding: '20px' }}>
            <h3 style={{ color: colors.primary, marginTop: 0 }}>{name || 'Organisation Name'}</h3>
            <p style={{ color: colors.text, fontSize: '14px', lineHeight: 1.6 }}>
              This is how your community platform will look with this colour palette.
            </p>
            <button style={{ backgroundColor: colors.primary, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
              Join Event
            </button>
            <span style={{ display: 'inline-block', marginLeft: '10px', color: colors.secondary, fontWeight: 600, cursor: 'pointer' }}>
              Learn More →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandingEditor;
